import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageTitle } from '@/components/PageTitle';
import { useGetAlert } from '../hooks/useAdminAlert';
import { useSiteData } from '../hooks/useSiteData';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Download,
  NotepadText,
  BadgeCheck,
  Badge,
  PersonStandingIcon,
  Shirt,
  Users,
  LinkIcon,
  Tag,
  Car,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEventImageUrl } from '@/lib/api/monitoring/events';
import { IntruderProfile, ContactEventDto } from '../alertApi';
import { getInvolvedHumans } from '../utils';
import { getCameraFeedName } from '@/lib/video-processor';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import QRCode from 'qrcode';
import { getTimeZoneName } from '@/lib/time';
import { mapboxToken } from '@/components/maps/mapbox';
import { useAuthStore, TenantDetails } from '@/store/authStore';
import { LogoType, logoUrls } from '@/app/Tenants/lookupTenantTheme';
import { formatNoteForActivityLog, extractTaggedEvents } from '@/lib/tagging';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { addAlertNote } from '@/lib/api/monitoring/alert';
import { useToast } from '@/hooks/use-toast';

// Component to handle intruder image loading with optional bounding box overlay
function IntruderImage({
  eventValueId,
  alt,
  className,
  boundingBox,
}: {
  eventValueId?: string;
  alt: string;
  className: string;
  boundingBox?: {
    topPct?: number;
    leftPct?: number;
    widthPct?: number;
    heightPct?: number;
  };
}) {
  const { data: imageUrl, isLoading } = useQuery({
    queryKey: ['eventImageUrl', eventValueId],
    queryFn: () => fetchEventImageUrl(eventValueId!),
    enabled: !!eventValueId,
  });

  if (isLoading || !imageUrl?.url) {
    return (
      <div className={`${className} flex items-center justify-center text-xs text-gray-500`}>
        Loading
      </div>
    );
  }

  const hasBoundingBox =
    boundingBox &&
    boundingBox.topPct != null &&
    boundingBox.leftPct != null &&
    boundingBox.widthPct != null &&
    boundingBox.heightPct != null;

  if (!hasBoundingBox) {
    return (
      <img
        src={imageUrl.url}
        alt={alt}
        className={className}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
        }}
      />
    );
  }

  const PAD = 2;
  const top = Math.max(0, boundingBox.topPct! - PAD);
  const left = Math.max(0, boundingBox.leftPct! - PAD);
  const width = Math.min(100 - left, boundingBox.widthPct! + PAD * 2);
  const height = Math.min(100 - top, boundingBox.heightPct! + PAD * 2);

  return (
    <div className="relative inline-block">
      <img
        src={imageUrl.url}
        alt={alt}
        className={className}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
        }}
      />
      <div
        className="pointer-events-none absolute rounded-sm border-2 border-red-500"
        style={{
          top: `${top}%`,
          left: `${left}%`,
          width: `${width}%`,
          height: `${height}%`,
        }}
      />
    </div>
  );
}

// Reusable component for displaying a labeled detail field
function DetailField({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div >
      <p className="min-w-[3.5rem] font-medium text-gray-700">{label}:</p>
      <p className="first-letter:capitalize">{value}</p>
    </div>
  );
}

// Reusable component for clothing detail fields (slightly narrower label)
function ClothingField({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="">
      <p className="min-w-[3rem] font-medium text-gray-700">{label}:</p>
      <p className="first-letter:capitalize">{value}</p>
    </div>
  );
}

function TenantLogo({ tenantDetails }: { tenantDetails?: TenantDetails }) {
  const [hasError, setHasError] = useState(false);
  const src = tenantDetails?.themePath
    ? logoUrls(tenantDetails)[LogoType.LightPrimary]
    : '/logo.svg';

  if (hasError) return null;

  return (
    <img
      className="h-12 max-w-24 object-contain"
      src={src}
      alt={tenantDetails?.tenantName || 'Watchful'}
      onError={() => setHasError(true)}
    />
  );
}

interface ReportConfig {
  // Detail fields
  startTime: boolean;
  actioned: boolean;
  closed: boolean;
  duration: boolean;
  responseTime: boolean;
  reviewedBy: boolean;
  cameraFeed: boolean;
  site: boolean;
  customer: boolean;
  timezone: boolean;
  // Sections
  activityLog: boolean;
  response: boolean;
  intruders: boolean;
  evidence: boolean;
  taggedEvents: boolean;
  // Content
  footerText: string;
}

const DEFAULT_REPORT_CONFIG: ReportConfig = {
  startTime: true,
  actioned: true,
  closed: true,
  duration: true,
  responseTime: true,
  reviewedBy: true,
  cameraFeed: true,
  site: true,
  customer: true,
  timezone: true,
  activityLog: true,
  response: true,
  intruders: true,
  evidence: true,
  taggedEvents: true,
  footerText: '',
};

// All boolean keys in ReportConfig (for select/deselect all)
const REPORT_CONFIG_BOOLEAN_KEYS: (keyof ReportConfig)[] = [
  'startTime',
  'actioned',
  'closed',
  'duration',
  'responseTime',
  'reviewedBy',
  'cameraFeed',
  'site',
  'customer',
  'timezone',
  'activityLog',
  'response',
  'intruders',
  'evidence',
  'taggedEvents',
];

interface VideoLink {
  id: string;
  url: string;
  title: string;
  description: string;
  qrCodeDataUrl: string;
}

interface ActivityItem {
  type: 'note' | 'contact' | 'system';
  date: Date;
  content?: string;
  author?: string;
  event?: ContactEventDto;
}

// Text snippets to filter out from notes in the audit log
const FILTERED_NOTE_PATTERNS = [
  'Added Offender',
  'Quill marked event',
  'Analysis completed by Quill',
  'Notified Monitoring Company',
  'Monitoring Company',
  'Email Sent to Monitoring Company',
  'Alert was re-opened as another event occurred',
  '[EVIDENCE',
  '[REMOVE_EVIDENCE',
  '[REPORT_CONFIG]',
  '[START_TIME_OVERRIDE]',
];

type GuardInfo = {
  guardRequested: boolean;
  guardDispatchCode?: string;
  guardRequired?: boolean;
  guardInstructions?: string;
  hasGuardInfo: boolean;
};

interface VehicleInfo {
  vehicleType?: string;
  vehicleColor?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  licensePlate?: string;
  occupants?: string;
  vehicleDistinguishingFeatures?: string;
}

function parseNoteField(note: string, field: string): string | undefined {
  const regex = new RegExp(`^${field}:\\s*(.+)$`, 'm');
  const match = note.match(regex);
  const value = match?.[1]?.trim();
  return value && value !== 'N/A' ? value : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getVehicleInfoFromNotes(alert: any): Map<number, VehicleInfo> {
  const vehicleMap = new Map<number, VehicleInfo>();
  if (!alert.recentNotes?.length) return vehicleMap;

  const vehicleNotes = alert.recentNotes
    .filter((n: { note?: string }) => n.note?.includes('Vehicle Type:'))
    .filter((n: { note?: string }) => n.note?.match(/(?:Added|Updated) Offender #\d+/));

  for (const n of vehicleNotes) {
    const numMatch = n.note.match(/(?:Added|Updated) Offender #(\d+)/);
    if (!numMatch) continue;
    const offenderNum = parseInt(numMatch[1], 10);
    vehicleMap.set(offenderNum, {
      vehicleType: parseNoteField(n.note, 'Vehicle Type'),
      vehicleColor: parseNoteField(n.note, 'Vehicle Color'),
      vehicleMake: parseNoteField(n.note, 'Vehicle Make'),
      vehicleModel: parseNoteField(n.note, 'Vehicle Model'),
      licensePlate: parseNoteField(n.note, 'License Plate'),
      occupants: parseNoteField(n.note, 'Occupants'),
      vehicleDistinguishingFeatures: parseNoteField(n.note, 'Vehicle Distinguishing Features'),
    });
  }
  return vehicleMap;
}

function getReportTitle(status?: string, tenantName?: string): string {
  const name = tenantName || 'Watchful';
  const titles: Record<string, string> = {
    Intruder: `Intruder Detected by ${name}`,
    SuspiciousPerson: `Suspicious Activity Detected by ${name}`,
    PossiblyStaff: `Possible Staff Activity Detected by ${name}`,
    ConfirmedStaff: `Staff Activity Confirmed by ${name}`,
    Weather: `Weather Event Detected by ${name}`,
    AnimalOrInsect: `Animal or Insect Activity Detected by ${name}`,
    Environment: `Environmental Event Detected by ${name}`,
    LightReflection: `Light Reflection Detected by ${name}`,
    FalsePositive: `False Positive Identified by ${name}`,
    NoThreat: `No Threat Identified by ${name}`,
    NeedsService: `Service Required - Detected by ${name}`,
    IncorrectMotionGrids: `Motion Grid Issue Detected by ${name}`,
    SafetyEvent: `Safety Event Detected by ${name}`,
    New: `Alert Under Review by ${name}`,
    Unknown: `Unclassified Event Detected by ${name}`,
    Other: `Event Detected by ${name}`,
  };
  return titles[status || ''] || `Event Detected by ${name}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getGuardInfo(alert: any): GuardInfo {
  const guardNote = alert.recentNotes?.find(
    (note: { note?: string }) =>
      note.note?.includes('Guard has been') &&
      (note.note.includes('requested') || note.note.includes('not requested'))
  );

  const guardRequested = guardNote?.note?.includes('Guard has been requested') ?? false;
  const guardDispatchCode = alert.project?.guardResponseDispatchCode;
  const guardRequired = alert.project?.guardResponseRequired;

  // Extract instructions from guard note (everything after first line)
  const guardInstructions = guardNote?.note
    ? guardNote.note.split('\n').slice(1).join('\n').trim() || undefined
    : undefined;

  const hasGuardInfo = !!(guardNote || guardDispatchCode || guardRequired !== undefined);

  return {
    guardRequested,
    guardDispatchCode,
    guardRequired,
    guardInstructions,
    hasGuardInfo,
  };
}

// Clean up note content for display - uses shared utility
const formatNoteContent = formatNoteForActivityLog;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildActivityItems(alert: any): ActivityItem[] {
  const activityItems: ActivityItem[] = [];

  // Add filtered notes
  if (alert.recentNotes) {
    alert.recentNotes
      .filter((note: { note?: string; author?: string }) => {
        const noteText = note.note || '';
        const author = note.author || '';
        // Filter out AI notes and notes matching filtered patterns
        if (author.toLowerCase() === 'ai') return false;
        return !FILTERED_NOTE_PATTERNS.some((pattern) => noteText.includes(pattern));
      })
      .forEach((note: { date?: string; note?: string; author?: string }) => {
        const formattedContent = formatNoteContent(note.note || '');
        // Skip if content is empty after formatting
        if (!formattedContent) return;

        activityItems.push({
          type: 'note',
          date: new Date(note.date || ''),
          content: formattedContent,
          author: note.author,
        });
      });
  }

  // Add contact events (notifications) - also filtered
  if (alert.contactEvents) {
    alert.contactEvents
      .filter((event: ContactEventDto) => {
        const eventBody = event.body || '';
        return !FILTERED_NOTE_PATTERNS.some((pattern) => eventBody.includes(pattern));
      })
      .forEach((event: ContactEventDto) => {
        activityItems.push({
          type: 'contact',
          date: new Date(event.occurred || ''),
          content: event.body,
          event,
        });
      });
  }

  // Add system event: Event started
  if (alert.occurred) {
    activityItems.push({
      type: 'system',
      date: new Date(alert.occurred),
      content: 'Event started',
      author: 'System',
    });
  }

  // Sort by date descending
  return activityItems.sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function AlertReportPage() {
  const { alertId } = useParams<{ alertId: string }>();
  const { data: alert, isLoading, error } = useGetAlert(alertId!);
  const { data: siteData } = useSiteData(alert?.projectId, alert?.companyId);
  const tenantDetails = useAuthStore((state) => state.tenant);
  const user = useAuthStore((state) => state.user);
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Agent';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [incidentVideoInitialized, setIncidentVideoInitialized] = useState(false);

  // Report config state
  const [reportConfig, setReportConfig] = useState<ReportConfig>(DEFAULT_REPORT_CONFIG);
  const [configInitialized, setConfigInitialized] = useState(false);

  // Parse saved report config from notes (latest [REPORT_CONFIG] note wins)
  const savedReportConfig = React.useMemo(() => {
    if (!alert?.recentNotes) return null;

    const sortedNotes = [...alert.recentNotes].sort(
      (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
    );

    for (const note of sortedNotes) {
      const noteText = note.note || '';
      if (noteText.startsWith('[REPORT_CONFIG]')) {
        try {
          const json = noteText.replace('[REPORT_CONFIG]', '').trim();
          return JSON.parse(json) as Partial<ReportConfig>;
        } catch {
          // Invalid JSON, skip
        }
      }
    }

    return null;
  }, [alert?.recentNotes]);

  // Parse start time override from notes (latest [START_TIME_OVERRIDE] note wins)
  const startTimeOverride = React.useMemo(() => {
    if (!alert?.recentNotes) return null;

    const sortedNotes = [...alert.recentNotes].sort(
      (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
    );

    for (const note of sortedNotes) {
      const noteText = note.note || '';
      if (noteText.startsWith('[START_TIME_OVERRIDE]')) {
        const isoString = noteText.replace('[START_TIME_OVERRIDE]', '').trim();
        const parsed = new Date(isoString);
        if (!isNaN(parsed.getTime())) return parsed;
      }
    }
    return null;
  }, [alert?.recentNotes]);

  const effectiveStartTime = startTimeOverride || (alert?.occurred ? new Date(alert.occurred) : null);

  // Default footer text using tenant name
  const defaultFooterText = `This report was generated automatically by ${tenantDetails?.tenantName || 'Watchful Monitoring'}`;

  // Initialize config from saved note on first load, or set tenant-aware defaults
  useEffect(() => {
    if (!configInitialized && alert) {
      if (savedReportConfig) {
        setReportConfig((prev) => ({
          ...prev,
          footerText: defaultFooterText,
          ...savedReportConfig,
        }));
      } else {
        setReportConfig((prev) => ({ ...prev, footerText: defaultFooterText }));
      }
      setConfigInitialized(true);
    }
  }, [savedReportConfig, configInitialized, alert, defaultFooterText]);

  // Save report config mutation
  const saveConfigMutation = useMutation({
    mutationFn: () => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId) {
        throw new Error('Missing alert data');
      }
      return addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: `[REPORT_CONFIG]${JSON.stringify(reportConfig)}`,
        author: userFullName,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert?.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert?.id] });
      toast({ title: 'Report settings saved' });
    },
    onError: () => {
      toast({ title: 'Failed to save report settings', variant: 'destructive' });
    },
  });

  const toggleConfigField = (field: keyof ReportConfig) => {
    setReportConfig((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const selectAllConfig = () => {
    setReportConfig((prev) => {
      const updated = { ...prev };
      for (const key of REPORT_CONFIG_BOOLEAN_KEYS) {
        (updated[key] as boolean) = true;
      }
      return updated;
    });
  };

  const deselectAllConfig = () => {
    setReportConfig((prev) => {
      const updated = { ...prev };
      for (const key of REPORT_CONFIG_BOOLEAN_KEYS) {
        (updated[key] as boolean) = false;
      }
      return updated;
    });
  };

  // Shorthand for the footer text from config
  const footerText = reportConfig.footerText;

  // Parse evidence links from notes (with QR code generation)
  const [evidenceLinksWithQR, setEvidenceLinksWithQR] = useState<VideoLink[]>([]);

  // Extract evidence links from notes using incremental IDs
  const evidenceLinksFromNotes = React.useMemo(() => {
    if (!alert?.recentNotes) return [];

    const linkMap = new Map<
      number,
      { id: number; url: string; title: string; description: string }
    >();
    const removedIds = new Set<number>();
    let nextId = 1;

    // Sort notes by date ascending to process in order
    const sortedNotes = [...alert.recentNotes].sort(
      (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
    );

    for (const note of sortedNotes) {
      const noteText = note.note || '';

      // Check for REMOVE_EVIDENCE_N (new format)
      const removeIdMatch = noteText.match(/^\[REMOVE_EVIDENCE_(\d+)\]/);
      if (removeIdMatch) {
        const id = parseInt(removeIdMatch[1], 10);
        removedIds.add(id);
        linkMap.delete(id);
        continue;
      }

      // Check for REMOVE_EVIDENCE:url (legacy format)
      const removeUrlMatch = noteText.match(/^\[REMOVE_EVIDENCE:([^\]]+)\]/);
      if (removeUrlMatch) {
        const url = removeUrlMatch[1];
        for (const [key, link] of linkMap) {
          if (link.url === url) {
            removedIds.add(key);
            linkMap.delete(key);
          }
        }
        continue;
      }

      // Parse evidence content helper
      const parseContent = (content: string) => {
        const lines = content.split('\n');
        let url = '';
        let title = '';
        let description = '';
        for (const line of lines) {
          if (line.startsWith('URL:')) {
            url = line.replace('URL:', '').trim();
          } else if (line.startsWith('Title:')) {
            title = line.replace('Title:', '').trim();
          } else if (line.startsWith('Description:')) {
            description = line.replace('Description:', '').trim();
          }
        }
        return { url, title, description };
      };

      // Check for EVIDENCE_N (new format)
      const evidenceIdMatch = noteText.match(/^\[EVIDENCE_(\d+)\]/);
      if (evidenceIdMatch) {
        const evidenceId = parseInt(evidenceIdMatch[1], 10);
        nextId = Math.max(nextId, evidenceId + 1);
        if (!removedIds.has(evidenceId)) {
          const content = noteText.replace(/^\[EVIDENCE_\d+\]/, '').trim();
          const parsed = parseContent(content);
          if (parsed.url) {
            linkMap.set(evidenceId, { id: evidenceId, ...parsed });
          }
        }
        continue;
      }

      // Check for EVIDENCE (legacy format without ID)
      if (noteText.startsWith('[EVIDENCE]')) {
        const evidenceId = nextId++;
        if (!removedIds.has(evidenceId)) {
          const content = noteText.replace('[EVIDENCE]', '').trim();
          const parsed = parseContent(content);
          if (parsed.url) {
            linkMap.set(evidenceId, { id: evidenceId, ...parsed });
          }
        }
        continue;
      }
    }

    return Array.from(linkMap.values());
  }, [alert?.recentNotes]);

  // Generate QR codes for evidence links
  useEffect(() => {
    const generateQRCodes = async () => {
      const linksWithQR = await Promise.all(
        evidenceLinksFromNotes.map(async (link) => {
          let qrCodeDataUrl = '';
          if (link.url) {
            try {
              qrCodeDataUrl = await QRCode.toDataURL(link.url, {
                width: 100,
                margin: 1,
                color: { dark: '#000000', light: '#ffffff' },
              });
            } catch (e) {
              console.error('Failed to generate QR code:', e);
            }
          }
          return {
            id: `evidence-${link.id}`,
            url: link.url,
            title: link.title,
            description: link.description,
            qrCodeDataUrl,
          };
        })
      );
      setEvidenceLinksWithQR(linksWithQR);
    };

    generateQRCodes();
  }, [evidenceLinksFromNotes]);

  // Extract tagged events from notes - uses shared utility
  const taggedEvents = React.useMemo(
    () => extractTaggedEvents(alert?.recentNotes, alert?.events),
    [alert?.recentNotes, alert?.events]
  );

  // Auto-populate incident video link when alert has a ready video clip
  useEffect(() => {
    const initIncidentVideo = async () => {
      // Access videoClip via type assertion since the API returns it but the type doesn't include it
      const videoClip = (
        alert?.intruderEventData as {
          videoClip?: { status?: string; cloudflareDownloadUrl?: string; expiresAt?: string };
        }
      )?.videoClip;

      if (
        !incidentVideoInitialized &&
        videoClip?.status === 'Ready' &&
        videoClip?.cloudflareDownloadUrl
      ) {
        const url = videoClip.cloudflareDownloadUrl;
        const qrCode = await generateQrCode(url);

        setEvidenceLinksWithQR((prev: VideoLink[]) => {
          // Check if already added
          if (prev.some((link: VideoLink) => link.url === url)) return prev;

          return [
            {
              id: 'incident-video',
              url,
              title: 'Incident Video',
              description: videoClip.expiresAt
                ? `Video expires: ${new Date(videoClip.expiresAt).toLocaleDateString('en-US', { timeZone: siteData?.timeZone || undefined })}`
                : 'Video recording of the incident',
              qrCodeDataUrl: qrCode,
            },
            ...prev,
          ];
        });
        setIncidentVideoInitialized(true);
      }
    };

    initIncidentVideo();
  }, [alert, incidentVideoInitialized, siteData?.timeZone]);

  // Generate QR code for a specific video link
  const generateQrCode = async (url: string): Promise<string> => {
    if (!url.trim()) return '';
    try {
      return await QRCode.toDataURL(url, {
        width: 96,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#111]">
        <div className="text-white">Loading report...</div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-md rounded-lg border border-gray-700 bg-gray-900 p-6">
          <h1 className="mb-4 text-xl font-semibold text-white">Report Not Found</h1>
          <p className="mb-4 text-gray-400">
            Unable to load the report for this alert. The alert may not exist or you may not have
            permission to view it.
          </p>
          <Link to="/monitoring-overview">
            <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Monitoring
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Compute guard info once for use throughout the report
  const guardInfo = getGuardInfo(alert);
  const vehicleInfoMap = getVehicleInfoFromNotes(alert);

  const policeCalled = (() => {
    if (!alert.recentNotes?.length) return false;
    const sorted = [...alert.recentNotes]
      .filter(
        (n: { note?: string }) =>
          n.note?.includes('Police called') || n.note?.includes('Police call unmarked')
      )
      .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    return (
      sorted[0]?.note?.includes('Police called') &&
      !sorted[0]?.note?.includes('Police call unmarked')
    );
  })();

  return (
    <div>
      <PageTitle title={`Alert Report ${alert.alertId}`} />
      <div className="flex min-h-screen bg-[#111]">
        <div className="print:hidden">
          {/* Print controls - hidden when printing */}
          <div className="sticky top-[56px] h-[calc(100vh-56px)] w-[360px] overflow-y-auto border-r border-gray-700 bg-[#111]">
            <div className="flex h-14 items-center gap-2 border-b bg-[#111] px-4 print:hidden">
              <NotepadText className="h-5 w-5" />
              <h1 className="font-semibold text-white">Alert Report - {alert.alertId}</h1>
            </div>
            <div className="mx-auto flex min-w-[320px] flex-col">
              {' '}
              <div className="flex flex-col gap-2 border-b p-4">
                <h2 className="pb-2 font-semibold print:break-after-avoid">Export options</h2>
                <Button onClick={handlePrint} className="">
                  <Download className="mr-2 h-4 w-4" />
                  Export Report
                </Button>
              </div>
              <Accordion type="single" collapsible>
                <AccordionItem value="item-1">
                  <AccordionTrigger>Customise Content</AccordionTrigger>
                  <AccordionContent className="">
                    <div className="flex items-center justify-between gap-2 px-4">
                      <Button variant="outline" className="flex-1" onClick={selectAllConfig}>
                        Select all
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={deselectAllConfig}>
                        Deselect all
                      </Button>
                    </div>
                    <h2 className="p-4 pb-2 text-sm font-semibold text-white">Alert Details</h2>
                    <section className="grid grid-cols-2">
                      {(
                        [
                          ['startTime', 'Start Time', !!effectiveStartTime],
                          ['actioned', 'Actioned', !!alert.actionedAt],
                          ['closed', 'Resolved', !!alert.closedAt],
                          ['duration', 'Duration', !!(effectiveStartTime && alert.closedAt)],
                          ['responseTime', 'Response Time', !!(effectiveStartTime && alert.actionedAt)],
                          ['reviewedBy', 'Reviewed By', getInvolvedHumans(alert).length > 0],
                          ['cameraFeed', 'Camera Feed', !!getCameraFeedName(alert.events)],
                          ['site', 'Site', !!alert.project?.name],
                          ['customer', 'Customer', !!alert.companyName],
                          ['timezone', 'Timezone', !!siteData?.timeZone],
                        ] as [keyof ReportConfig, string, boolean][]
                      )
                        .filter(([, , hasData]) => hasData)
                        .map(([key, label]) => (
                          <div key={key}>
                            <Label
                              htmlFor={`rc-${key}`}
                              className="flex cursor-pointer items-center gap-2 p-4 py-3"
                            >
                              <Checkbox
                                id={`rc-${key}`}
                                checked={reportConfig[key] as boolean}
                                onCheckedChange={() => toggleConfigField(key)}
                              />{' '}
                              {label}
                            </Label>
                          </div>
                        ))}
                    </section>
                    <h2 className="p-4 pb-2 text-sm font-semibold text-white">Sections</h2>
                    <section className="grid grid-cols-2">
                      {(
                        [
                          [
                            'activityLog',
                            'Activity Log',
                            !!(alert.recentNotes?.length || alert.contactEvents?.length),
                          ],
                          ['response', 'Response', true],
                          [
                            'intruders',
                            'Intruders',
                            !!(
                              alert.intruderEventData?.intruders &&
                              alert.intruderEventData.intruders.length > 0
                            ),
                          ],
                          ['evidence', 'Evidence', evidenceLinksWithQR.length > 0],
                          ['taggedEvents', 'Tagged Events', taggedEvents.length > 0],
                        ] as [keyof ReportConfig, string, boolean][]
                      )
                        .filter(([, , hasData]) => hasData)
                        .map(([key, label]) => (
                          <div key={key}>
                            <Label
                              htmlFor={`rc-${key}`}
                              className="flex cursor-pointer items-center gap-2 p-4 py-3"
                            >
                              <Checkbox
                                id={`rc-${key}`}
                                checked={reportConfig[key] as boolean}
                                onCheckedChange={() => toggleConfigField(key)}
                              />{' '}
                              {label}
                            </Label>
                          </div>
                        ))}
                    </section>

                    <div className="m-4 space-y-3">
                      <label
                        htmlFor="footer-text"
                        className="block text-sm font-semibold text-white"
                      >
                        Footer Text
                      </label>
                      <textarea
                        id="footer-text"
                        value={reportConfig.footerText}
                        onChange={(e) =>
                          setReportConfig((prev) => ({ ...prev, footerText: e.target.value }))
                        }
                        className="w-full resize-none rounded-md border border-gray-600 bg-gray-800 p-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="Enter footer text for the report..."
                      />
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => saveConfigMutation.mutate()}
                        disabled={saveConfigMutation.isPending}
                      >
                        {saveConfigMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Report content */}
        <div
          className="printingproperties min-h-screen w-full bg-[#222] text-white print:min-h-0 print:bg-white print:text-black"
          style={{ fontFamily: 'TT Firs Neue, sans-serif' }}
          data-report-content
        >
          <div className="mt-12 print:hidden"></div>
          {/* A4 Paper Container */}
          <div className="mx-auto min-h-[29.7cm] max-w-[21cm] bg-white p-8 text-black print:min-h-0 print:p-6 print:shadow-none">
            {/* Report Header */}
            <div className="mb-4 flex items-center justify-between gap-4 border-b border-gray-100 pb-4 print:mb-4">
              <div>
                <h1 className="text-lg font-medium tracking-tight text-black">
                  {getReportTitle(alert.status, tenantDetails?.tenantName)}
                </h1>
                <p className="text-sm text-gray-600">
                  Report generated{' '}
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: siteData?.timeZone || undefined,
                  })}
                </p>
              </div>{' '}
              <TenantLogo tenantDetails={tenantDetails ?? undefined} />
            </div>

            <section className="grid grid-cols-2 gap-2">
              {/* Site Information Section */}
              {alert.project && (
                <div className="rounded-lg bg-[#f7f7f7] print:break-inside-avoid">
                  <h2 className="border-gray-300 p-2 text-sm font-medium tracking-tight text-black print:break-after-avoid">
                    Location
                  </h2>
                  <div className="m-2 mt-0 grid grid-cols-2 gap-x-8 gap-y-3 text-[10px]">
                    {reportConfig.site && (
                      <div>
                        <p className="font-medium text-gray-700">Site :</p>
                        <p className="">{alert.project.name || 'N/A'}</p>
                      </div>
                    )}

                    {reportConfig.customer && (
                      <div>
                        <p className="font-medium text-gray-700">Customer:</p>
                        <p className="">{alert.companyName || 'N/A'}</p>
                      </div>
                    )}
                    {alert.activationZone && (
                      <div>
                        <p className="font-medium text-gray-700">Zone:</p>
                        <p className="">{alert.activationZone || 'N/A'}</p>
                      </div>
                    )}
                    {reportConfig.cameraFeed &&
                      (() => {
                        const cameraName = getCameraFeedName(alert.events);
                        if (!cameraName) return null;
                        return (
                          <div>
                            <p className="font-medium text-gray-700">First seen on:</p>
                            <p className="">{cameraName}</p>
                          </div>
                        );
                      })()}
                    <div className="col-span-2">
                      <p className="font-medium text-gray-700">Address:</p>
                      <p className="">
                        {(() => {
                          const address = siteData?.address;

                          if (address) {
                            const addressParts = [
                              address.addressLine1,
                              address.addressLine2,
                              address.city,
                              address.postCode,
                            ].filter(Boolean);
                            return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
                          }

                          return 'N/A';
                        })()}
                      </p>
                    </div>
                  </div>
                  {siteData?.address?.lat && siteData?.address?.lon ? (
                    <div className="m-2 overflow-hidden rounded-lg">
                      <img
                        src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s-marker+D1F551(${siteData.address.lon},${siteData.address.lat})/${siteData.address.lon},${siteData.address.lat},12,0/280x240@2x?access_token=${mapboxToken}`}
                        alt="Site location map"
                        className="h-[252px] w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="m-2 flex h-[240px] items-center justify-center rounded-lg bg-gray-200">
                      <p className="text-sm text-gray-500">No map available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Alert Information Section */}
              <div className="rounded-sm bg-[#f7f7f7] print:break-inside-avoid">
                <h2 className="border-gray-300 p-2 text-sm font-medium text-black print:break-after-avoid">
                  Details
                </h2>
                <div className="mb-[16px] grid grid-cols-2 gap-x-8 gap-y-3 p-2 pt-0 text-[10px]">
                  {reportConfig.startTime && effectiveStartTime && (
                    <div>
                      <p className="font-medium text-gray-700">Started</p>
                      <p className="">
                        {' '}
                        {effectiveStartTime.toLocaleTimeString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: siteData?.timeZone || undefined,
                        })}
                      </p>
                    </div>
                  )}{' '}
                  {reportConfig.responseTime && effectiveStartTime && alert.actionedAt && (
                    <div>
                      <p className="font-medium text-gray-700">Response time</p>
                      <p className="">
                        {(() => {
                          const diffMs =
                            new Date(alert.actionedAt).getTime() -
                            effectiveStartTime.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const hours = Math.floor(diffMins / 60);
                          const mins = diffMins % 60;
                          if (hours > 0) {
                            return `${hours}h ${mins}m`;
                          }
                          if (mins === 0) {
                            return 'Under 1m';
                          }
                          return `${mins}m`;
                        })()}
                      </p>
                    </div>
                  )}
                  {reportConfig.actioned && alert.actionedAt && (
                    <div>
                      <p className="font-medium text-gray-700">Actioned</p>
                      <p className="">
                        {new Date(alert.actionedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: siteData?.timeZone || undefined,
                        })}
                      </p>
                    </div>
                  )}{' '}
                  {reportConfig.duration && effectiveStartTime && alert.closedAt && (
                    <div>
                      <p className="font-medium text-gray-700">Duration</p>
                      <p className="">
                        {(() => {
                          const diffMs =
                            new Date(alert.closedAt).getTime() - effectiveStartTime.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const hours = Math.floor(diffMins / 60);
                          const mins = diffMins % 60;
                          if (hours > 0) {
                            return `${hours}h ${mins}m`;
                          }
                          if (mins === 0) {
                            return 'Under 1m';
                          }
                          return `${mins}m`;
                        })()}
                      </p>
                    </div>
                  )}
                  {reportConfig.closed && alert.closedAt && (
                    <div>
                      <p className="font-medium text-gray-700">Resolved</p>
                      <p className="">
                        {new Date(alert.closedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: siteData?.timeZone || undefined,
                        })}
                      </p>
                    </div>
                  )}
                  {reportConfig.timezone && siteData?.timeZone && (
                    <div>
                      <p className="font-medium text-gray-700">Timezone:</p>
                      <p className="">
                        {getTimeZoneName(siteData.timeZone)} - {siteData.timeZone}
                      </p>
                    </div>
                  )}{' '}
                  <div>
                    <p className="font-medium text-gray-700">Incident Type:</p>
                    <p className="">{alert.status || 'N/A'}</p>
                  </div>
                  {reportConfig.reviewedBy && getInvolvedHumans(alert).length > 0 && (
                    <div>
                      <p className="font-medium text-gray-700">Reviewed By:</p>
                      <p className="">{getInvolvedHumans(alert).join(', ')}</p>
                    </div>
                  )}
                </div>
                <div className="m-2 mt-2 border-t border-t-gray-300 pt-4 pb-2">
                  <h2 className="mb-1 text-xs font-medium text-gray-700">Reference</h2>
                  <p className="text-[8px]">
                    To learn more about this incident, reference the Alert ID when talking with your
                    security company, and Police ID when talking to police
                  </p>
                  <div className="mt-2 grid grid-cols-2 justify-between gap-8 text-[10px]">
                    <div className="">
                      <p className="font-medium text-gray-700">Alert ID:</p>
                      <p className="">{alert.alertId || 'N/A'}</p>
                    </div>
                    {alert.intruderEventData?.policeId && (
                      <div className="">
                        <p className="font-medium text-gray-700">Police Event No.:</p>
                        <p className="">{alert.intruderEventData.policeId}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="m-2 flex items-center gap-2 rounded-lg border border-gray-300 bg-white p-2">
                  <div className="h-12 w-12">
                    <img alt="Snapshot" src="/icon/welcome.svg" />
                  </div>
                  <div className="text-[10px]">
                    <p>Scan the qr code or visit the link to learn more</p>
                    <p>support.watchfulhq.com</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Incident Details Section */}
            {reportConfig.response && (
              <div className="min-h-[12cm]">
                <div className="mt-2 mb-2 rounded-lg bg-[#f7f7f7] p-2 print:break-inside-avoid min-h-[9cm]">
                  <h2 className="border-gray-300 pb-2 text-sm font-medium tracking-tight text-black print:break-after-avoid">
                    Response
                  </h2>
                  <div className="flex gap-2 text-[10px] font-medium">
                    {alert.actionedBy ? (
                      <div className="border-green-dark bg-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-white">
                        <BadgeCheck className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Agent Reviewed</p>
                      </div>
                    ) : (
                      <div className="border-green-dark text-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2">
                        <Badge className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Agent Not Reviewed</p>
                      </div>
                    )}
                    {policeCalled ? (
                      <div className="border-green-dark bg-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-white">
                        <BadgeCheck className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Police Called</p>
                      </div>
                    ) : (
                      <div className="border-green-dark text-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2">
                        <Badge className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Police Not Called</p>
                      </div>
                    )}

                    {guardInfo.guardRequested ? (
                        <div className="border-green-dark bg-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-white">
                          <BadgeCheck className="h-4 w-4 shrink-0" />
                          <p className="whitespace-nowrap">Guard Dispatched</p>
                        </div>
                      ) : siteData?.guardResponseEnabled ? (
                        <div className="border-green-dark text-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2">
                          <Badge className="h-4 w-4 shrink-0" />
                          <p className="whitespace-nowrap">Guard Not Dispatched</p>
                        </div>
                      ) : null}

                    {alert.intruderEventData?.policeResponded ? (
                      <div className="border-green-dark bg-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-white">
                        <BadgeCheck className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Police Responded</p>
                      </div>
                    ) : (
                      <div className="border-green-dark text-green-dark flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2">
                        <Badge className="h-4 w-4 shrink-0" />
                        <p className="whitespace-nowrap">Police Response Unknown</p>
                      </div>
                    )}
                  </div>
<section className=" min-h-[7cm]">
                  {alert.intruderEventData?.eventOverview && (
                    <div className="mt-4 text-xs">
                      <span className="block font-medium text-gray-700">Event Overview:</span>
                      <p className="">{alert.intruderEventData.eventOverview}</p>
                    </div>
                  )}

           

                  {alert.intruderEventData?.closureResult && (
                    <div className="mt-4 mb-2 text-[10px]">
                      <span className="block font-medium text-gray-700">Closure Result:</span>
                      <p className="">{alert.intruderEventData.closureResult}</p>
                    </div>
                  )}

                  {!alert.closedAt && (
                    <div className="mt-4 mb-2 text-[10px]">
                      <span className="block font-medium text-gray-700">Closure Result:</span>
                      <p className="">
                        This alert has not been resolved and might still be ongoing.
                      </p>
                    </div>
                  )}</section>
                 
                </div>
                {(reportConfig.intruders || reportConfig.evidence || reportConfig.taggedEvents) && (
                <section className="min-h-[2cm] rounded-lg bg-[#f7f7f7] p-3 mb-6">
                    <h2 className="text-xs font-medium tracking-tight text-gray-700 print:break-after-avoid">
                      Available for review
                    </h2>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {reportConfig.intruders && (
                      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                        <Users className="h-4 w-4 shrink-0 text-gray-500" />
                        <div>
                          <p className="text-sm font-semibold leading-tight">
                            {alert.intruderEventData?.intruders?.length ?? 0}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {(alert.intruderEventData?.intruders?.length ?? 0) === 1
                              ? 'Documented Intruder'
                              : 'Documented Intruders'}
                          </p>
                        </div>
                      </div>
                      )}
                      {reportConfig.evidence && (
                      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                        <LinkIcon className="h-4 w-4 shrink-0 text-gray-500" />
                        <div>
                          <p className="text-sm font-semibold leading-tight">
                            {evidenceLinksWithQR.length}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {evidenceLinksWithQR.length === 1 ? 'Evidence Link' : 'Evidence Links'}
                          </p>
                        </div>
                      </div>
                      )}
                    
                        {reportConfig.taggedEvents && (
                        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                          <Tag className="h-4 w-4 shrink-0 text-gray-500" />
                          <div>
                            <p className="text-sm font-semibold leading-tight">
                              {taggedEvents.length}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              Tagged {taggedEvents.length === 1 ? 'Event' : 'Events'}
                            </p>
                          </div>
                        </div>
                        )}
                  
                    </div>
                  </section>
                )}
              </div>
            )}

            {reportConfig.evidence && evidenceLinksWithQR.length > 0 && (
              <>
                <h2 className="mb-4 border-b border-gray-300 pb-2 text-lg font-medium text-black print:break-after-avoid-page">
                  Evidence
                </h2>
                <p className="mb-6 text-sm">
                  {' '}
                  Evidence available by scanning the QR Code or by visiting the links below:
                </p>
                {evidenceLinksWithQR.map((link) => (
                  <div
                    key={link.id}
                    className="mb-6 flex items-center gap-4 border-b border-gray-300 pb-4 print:break-inside-avoid"
                  >
                    <div className="flex h-24 w-24 items-center justify-center rounded-md border border-gray-300 bg-white p-1">
                      {link.qrCodeDataUrl ? (
                        <img
                          src={link.qrCodeDataUrl}
                          alt="Video Link QR Code"
                          className="h-full w-full"
                        />
                      ) : (
                        <div className="text-center text-xs text-gray-500">QR Code</div>
                      )}
                    </div>
                    <div className="flex-1 text-sm">
                      <p className="font-semibold">{link.title}</p>

                      <a href={link.url} className="break-all text-blue-600 underline">
                        {link.url}
                      </a>
                      <p className="text-gray-600">{link.description}</p>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Intruder Details Section */}
            {reportConfig.intruders &&
              alert.intruderEventData?.intruders &&
              alert.intruderEventData.intruders.length > 0 && (
                <div className="mb-6 print:break-inside-avoid">
                  <div className="space-y-4">
                    {alert.intruderEventData.intruders.map(
                      (intruder: IntruderProfile, index: number) => (
                        <div
                          key={index}
                          className="print:page-break-inside-avoid border-b border-gray-300 pb-6 last:border-b-0 print:break-inside-avoid"
                        >
                       
                       
                            {/* Intruder Images */}
                            {intruder.images && intruder.images.length > 0 && (
                              <div className="w-full shrink-0 ">
                                <div className="flex flex-wrap gap-2">
                                  {intruder.images.slice(0, 3).map((image, imgIndex) => (
                                    <div key={imgIndex} className="relative">
                                      <IntruderImage
                                        eventValueId={image.eventValueId}
                                        alt={`Intruder ${index + 1} - Image ${imgIndex + 1}`}
                                        className="w-fit  border border-gray-300 object-cover rounded"
                                        boundingBox={image}
                                      />
                                    </div>
                                  ))}
                           
                                </div>
                              </div>
                            )}
                           
             
                          {(() => {
                            const vehicleInfo = vehicleInfoMap.get(index + 1);
                            if (vehicleInfo) {
                              return (
                                <>
                                  <h3 className="font-semibold text-gray-800  border-gray-100 py-2">Vehicle #{index + 1}</h3>
                                  <div className="bg-[#f7f7f7] border border-gray-100 rounded">
                                    <div className="flex items-center gap-1 p-2 border-b border-gray-100">
                                      <Car className="h-4 w-4 shrink-0 text-gray-500" />
                                      <h3 className="text-[11px] font-medium text-gray-800">Vehicle</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 p-2 text-[10px]">
                                      <DetailField label="Type" value={vehicleInfo.vehicleType} />
                                      <DetailField label="Color" value={vehicleInfo.vehicleColor} />
                                      <DetailField label="Make" value={vehicleInfo.vehicleMake} />
                                      <DetailField label="Model" value={vehicleInfo.vehicleModel} />
                                      <DetailField label="License Plate" value={vehicleInfo.licensePlate} />
                                      <DetailField label="Occupants" value={vehicleInfo.occupants} />
                                      <DetailField label="Distinguishing Features" value={vehicleInfo.vehicleDistinguishingFeatures} />
                                    </div>
                                  </div>
                                </>
                              );
                            }
                            return (
                              <>
                                <h3 className="font-semibold text-gray-800  border-gray-100 py-2">Intruder #{index + 1}</h3>
                                {(intruder.sex || intruder.age || intruder.build || intruder.height || intruder.ethnicity || intruder.hair ||
                                  intruder.clothingTop || intruder.clothingPants || intruder.clothingShoes || intruder.clothing) && (
                                <div className=" grid grid-cols-2 gap-2 text-xs">
                                  
                                  {(intruder.sex || intruder.age || intruder.build || intruder.height || intruder.ethnicity || intruder.hair) && (
                                  <div className="bg-[#f7f7f7] border border-gray-100 rounded">
                                    <div className="flex items-center gap-1 p-2 border-b border-gray-100">
                                      <PersonStandingIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                      <h3 className="text-[11px] font-medium text-gray-800">Person</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 p-2 text-[10px]">
                                      <DetailField label="Sex" value={intruder.sex} />
                                      <DetailField label="Age" value={intruder.age} />
                                      <DetailField label="Build" value={intruder.build} />
                                      <DetailField label="Height" value={intruder.height} />
                                      <DetailField label="Ethnicity" value={intruder.ethnicity} />
                                      <DetailField label="Hair" value={intruder.hair} />
                                    </div>
                                  </div>
                                  )}

                                  {(intruder.clothingTop || intruder.clothingPants || intruder.clothingShoes || intruder.clothing) && (
                                  <div className="border border-gray-100 bg-[#f7f7f7] rounded">
                                  <div className="flex items-center gap-1 p-2 border-b border-gray-100">
                                      <Shirt className="h-4 w-4 shrink-0 text-gray-500" />
                                      <h3 className="text-[11px] font-medium text-gray-800">Clothing</h3>
                                    </div>
                                 
                                  <div className="grid grid-cols-2 gap-4 p-2 text-[10px]">
                                      <ClothingField label="Top" value={intruder.clothingTop} />
                                      <ClothingField label="Pants" value={intruder.clothingPants} />
                                      <ClothingField label="Shoes" value={intruder.clothingShoes} />
                                      <ClothingField label="Items" value={intruder.clothing} />
                                    </div>
                                  </div>
                                  )}
                                </div>
                                )}
                              </>
                            );
                          })()}
                          {(intruder.otherInfo || (!vehicleInfoMap.has(index + 1) && intruder.description)) && (
                          <div className="border border-gray-100 bg-[#f7f7f7] rounded mt-2 p-2">
                          {intruder.otherInfo && (
                            <div className=" min-w-[20%] flex-1 text-[11px]">
                              <p className="mb-1 block font-medium text-gray-700">Other Info:</p>
                              <p className=" text-[10px] text-gray-800 normal-case first-letter:capitalize">
                                {intruder.otherInfo}
                              </p>
                            </div>
                          )}

                          {!vehicleInfoMap.has(index + 1) && intruder.description && (
                            <div className=" text-[10px]">
                              <span className="mb-1 block font-medium text-gray-700">
                                Description:
                              </span>
                              <p className=" text-[10px] text-gray-800 normal-case first-letter:capitalize">
                                {intruder.description}
                              </p>
                            </div>
                          )}
                          {intruder.otherInfo && (
                            <div className="mt-3 text-[10px]">
                              <span className="mb-1 block font-medium text-gray-700">
                                Additional Info:
                              </span>
                              <p className=" text-[10px] text-gray-800 normal-case first-letter:capitalize">
                                {intruder.otherInfo}
                              </p>
                            </div>
                          )}</div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Tagged Events Section */}
            {reportConfig.taggedEvents && taggedEvents.length > 0 && (
              <section className="mt-4 mb-8 rounded-lg print:break-inside-avoid">
                <div className="mb-2 flex items-center gap-1 border-b border-gray-300 pb-2">
                  <h2 className="font-medium tracking-tight print:break-after-avoid">
                    {taggedEvents.length} Tagged {taggedEvents.length > 1 ? 'Events' : 'Event'}
                  </h2>
                </div>
                <div className="flex flex-col gap-4 text-sm">
                  {taggedEvents.map((tagged, index) => (
                    <div
                      key={tagged.eventId}
                      className="flex flex-col border-b border-gray-200 pb-4 last:border-b-0 print:break-inside-avoid"
                    >
                      {tagged.imageUrl && (
                        <img
                          src={tagged.imageUrl}
                          alt={`Tagged event ${index + 1}`}
                          className="w-full rounded border border-gray-300 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="mt-2 flex flex-col">
                        <p className="text-xs font-medium text-gray-800">{tagged.comment}</p>
                        <p className="text-[10px] text-gray-500">
                          Tagged at{' '}
                          {tagged.date.toLocaleString('en-US', {
                            timeZone: siteData?.timeZone || undefined,
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          by {tagged.author && ` ${tagged.author}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {reportConfig.activityLog && (
              <div className="mt-4 mb-4 rounded-lg bg-[#f7f7f7] p-2 print:break-inside-avoid">
                <h2 className="border-gray-300 pb-2 text-sm font-medium tracking-tight text-black print:break-after-avoid">
                  Activity Log
                </h2>
                <div className="space-y-2">
                  {buildActivityItems(alert).map((item, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-200 pb-2 last:border-b-0 print:break-inside-avoid"
                    >
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="font-medium text-gray-700">
                          {item.type === 'contact'
                            ? item.event?.type === 'MonitoringNotified'
                              ? 'Notification'
                              : item.event?.type === 'ContactSms'
                                ? `SMS to ${item.event?.to || 'Unknown'}`
                                : item.event?.type === 'ContactSmsReply'
                                  ? `SMS from ${item.event?.to || 'Unknown'}`
                                  : item.event?.type === 'ContactCall'
                                    ? `Call to ${item.event?.to || 'Unknown'}`
                                    : item.event?.type === 'ContactEmail'
                                      ? `Email to ${item.event?.to || 'Unknown'}`
                                      : item.event?.type === 'Police'
                                        ? 'Police'
                                        : 'Contact'
                            : item.author === 'AI'
                              ? 'Quill'
                              : item.author || 'System'}
                        </span>
                        <span className="text-gray-500">
                          {item.date.toLocaleString('en-US', {
                            timeZone: siteData?.timeZone || undefined,
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="mt-1 text-[10px] whitespace-pre-wrap text-gray-800">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-12 border-t border-gray-300 pt-4 text-center">
              <p className="text-xs text-gray-500">{footerText}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
